<template>
	<section class="top-story" aria-label="国械头条">
		<div class="top-story-header">
			<h2 class="top-story-title">
				<i class="top-story-title-icon"></i>
				国械头条
			</h2>
			<a class="top-story-more" href="/gxzz/topstory" aria-label="国械头条更多" @click.prevent="handleClickMore">
				更多
				<i class="iconfont icon-gongneng_xiangyou-37"></i>
			</a>
		</div>

		<van-swipe
			v-if="list.length"
			ref="swipeRef"
			class="top-story-swipe"
			:autoplay="autoplay"
			:duration="500"
			:loop="totalDots > 1"
			:show-indicators="false"
			@change="onSwipeChange"
		>
			<van-swipe-item v-for="(page, pageIndex) in pages" :key="pageIndex">
				<ul class="list-col">
					<li v-for="(item, index) in page" :key="item.announcementId || item.id || `${pageIndex}-${index}`" class="list-item-wrap">
						<a
							:href="buildNoticeItemHref(item, { loggedIn: isLoggedIn, isGxzz: true })"
							:target="isExternalLink(item) ? '_blank' : undefined"
							:rel="isExternalLink(item) ? 'nofollow' : undefined"
							class="list-item"
							@click.prevent="handleItemClick($event, item)"
						>
							<div class="list-item-main">
								<ExpandableText customClass="list-item-title" :enableButton="false" :line="1" :dt="item.title || item.name" />
								<span class="list-item-time">{{ formatTime(item) }}</span>
							</div>
							<i class="iconfont icon-gongneng_xiangyou-37 list-item-arrow"></i>
						</a>
					</li>
				</ul>
			</van-swipe-item>
		</van-swipe>

		<div v-else class="top-story-empty">
			<img src="/staticSeo/images/no-data-new.png" alt="暂无头条数据" />
		</div>

		<div v-if="totalDots > 1" class="pagination-dots">
			<button
				v-for="index in totalDots"
				:key="index"
				type="button"
				class="dot-wrapper"
				:aria-label="`第${index}页`"
				@click.stop="goToSlide(index - 1)"
			>
				<span class="dot" :class="{ active: currentIndex === index - 1 }"></span>
			</button>
		</div>
	</section>
</template>

<script setup>
import { computed, ref } from 'vue';
import ExpandableText from '@/components/ExpandableText.vue';
import { isExternalLink } from '@/utils/link/externalLink';
import { buildNoticeItemHref } from '@/utils/link/noticeDetail';

const PAGE_SIZE = 5;
const AUTOPLAY_DELAY = 5000;

const props = defineProps({
	list: {
		type: Array,
		default: () => [],
	},
});

const list = computed(() => props.list || []);

const emit = defineEmits(['clickMore', 'toDetail']);

const swipeRef = ref(null);
const currentIndex = ref(0);

const pages = computed(() => {
	const result = [];
	for (let i = 0; i < list.value.length; i += PAGE_SIZE) {
		result.push(list.value.slice(i, i + PAGE_SIZE));
	}
	return result;
});

const totalDots = computed(() => pages.value.length);
const autoplay = computed(() => (totalDots.value > 1 ? AUTOPLAY_DELAY : 0));

/** 登录态用 cookie：SSR 与客户端首屏一致 */
const tokenCookie = useCookie('cmdc_access_token_mobile');
const isLoggedIn = computed(() => !!tokenCookie.value);

/** 展示时间：优先发布时间（后端字段为 publistTime） */
const formatTime = item =>
	item?.publistTime || item?.publishTime || item?.publishDate || item?.releaseTime || item?.pubTime || item?.updateTime || item?.createTime || '';

const onSwipeChange = index => {
	currentIndex.value = index;
};

const goToSlide = index => {
	swipeRef.value?.swipeTo?.(index);
	currentIndex.value = index;
};

const handleClickMore = () => {
	emit('clickMore');
};

/** 点击：埋点交给页面，跳转跟随 href */
const handleItemClick = (event, item) => {
	emit('toDetail', item);

	const href = event?.currentTarget?.getAttribute('href');
	if (!href || href === '#') return;

	if (isExternalLink(item)) {
		window.open(item.linkUrl, '_blank');
		return;
	}
	navigateTo(href);
};
</script>

<style scoped lang="scss">
.top-story {
	margin: 0 8px 8px;
	padding: 12px 12px 8px;
	background: #fff;
	border-radius: 5px;
}

.top-story-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
}

.top-story-title {
	display: flex;
	align-items: center;
	margin: 0;
	font-size: 15px;
	font-weight: 600;
	line-height: 20px;
	color: #1f1f1f;

	.top-story-title-icon {
		width: 3px;
		height: 14px;
		margin-right: 6px;
		border-radius: 2px;
		background: #455bd9;
	}
}

.top-story-more {
	display: flex;
	align-items: center;
	gap: 2px;
	font-size: 12px;
	color: #909399;
	text-decoration: none;

	.iconfont {
		font-size: 12px;
	}
}

.top-story-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 120px;
	padding: 8px 0 12px;

	img {
		width: 160px;
		height: auto;
	}
}

.list-col {
	width: 100%;
	margin: 0;
	padding: 0;
	list-style: none;

	li + li {
		border-top: 1px solid #f0f0f0;
	}
}

.list-item-wrap {
	position: relative;
}

.list-item {
	position: relative;
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 8px 0;
	text-decoration: none;
	color: inherit;
}

.list-item-main {
	flex: 1;
	min-width: 0;

	:deep(.expandable-text-container),
	:deep(.hide-container) {
		min-width: 0;
	}

	:deep(.list-item-title) {
		width: 100%;
		font-size: 13px;
		font-weight: 600;
		line-height: 18px;
		color: #333;
	}
}

.list-item-time {
	display: block;
	margin-top: 2px;
	font-size: 11px;
	line-height: 16px;
	color: #999;
}

.list-item-arrow {
	flex-shrink: 0;
	font-size: 12px;
	color: #c0c4cc;
}

.pagination-dots {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 4px;
	padding: 12px 0 4px;

	.dot-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 16px;
		padding: 0;
		border: none;
		background: transparent;
		cursor: pointer;
	}

	.dot {
		display: block;
		width: 6px;
		height: 4px;
		border-radius: 10px;
		background: #c8cdd4;
		transition:
			width 0.3s,
			background-color 0.3s;

		&.active {
			width: 12px;
			background: #455bd9;
		}
	}
}
</style>
